<template>
  <!--
    Task 11 / STEP-12 (Q-011): destination follows ACTING CONTEXT, not
    membership. A dual-role user who has not chosen yet is asked rather than
    sent somewhere arbitrary -- picking for them would silently decide which
    half of their account they came here to use.
  -->
  <div
    v-if="roleSelectionRequired"
    class="role-gate"
    data-testid="role-selection-required"
  >
    <!--
      Task 12 / STEP-04 (A, E). Previously a bare `.role-selection` div with no
      matching CSS anywhere and two identical full-width primary buttons, so
      the screen rendered unstyled and implied that the first option was the
      default. The two choices are now equal-weight cards -- the choice IS the
      action here, so neither is styled as a primary CTA -- each carrying a
      one-line description of what that role actually does.

      Same design language as the LoginView registration role choice
      (Task 12 / STEP-02) so the two role decisions in the product look like
      the same decision.
    -->
    <div class="role-gate__card">
      <h1 class="role-gate__heading">
        {{ $t('dashboard.roleSelection.header') }}
      </h1>
      <p class="role-gate__body">{{ $t('dashboard.roleSelection.body') }}</p>

      <div class="role-gate__choices">
        <button
          type="button"
          class="role-choice"
          data-testid="role-selection-student"
          :disabled="isSwitchingRole"
          @click="chooseRole('student')"
        >
          <span class="role-choice__label">
            {{ $t('dashboard.roleSelection.student') }}
          </span>
          <span class="role-choice__hint">
            {{ $t('dashboard.roleSelection.studentHint') }}
          </span>
        </button>
        <button
          type="button"
          class="role-choice"
          data-testid="role-selection-volunteer"
          :disabled="isSwitchingRole"
          @click="chooseRole('volunteer')"
        >
          <span class="role-choice__label">
            {{ $t('dashboard.roleSelection.volunteer') }}
          </span>
          <span class="role-choice__hint">
            {{ $t('dashboard.roleSelection.volunteerHint') }}
          </span>
        </button>
      </div>
    </div>
  </div>
  <div v-else>
    <!--
      The role bar is shown only to an identity that genuinely holds both
      roles, so single-role users see exactly what they saw before.

      Task 12 / STEP-04 (B, C). The switcher used to be the ONLY role cue on
      the page, which meant the user had to infer their current role from the
      inverse of a button label. The acting context is now named outright in
      visible text, and the switch is demoted to a secondary action beside it.
    -->
    <div v-if="isDualRole" class="role-bar" data-testid="role-bar">
      <p class="role-bar__current" data-testid="active-role-indicator">
        {{ activeRoleLabel }}
      </p>
      <button
        type="button"
        class="uc-link-button role-bar__switch"
        data-testid="role-switcher"
        :disabled="isSwitchingRole"
        @click="chooseRole(inactiveRole)"
      >
        {{ switchRoleLabel }}
      </button>
    </div>
    <volunteer-dashboard v-if="isVolunteer" />
    <student-dashboard v-else />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import VolunteerDashboard from './VolunteerDashboard/index.vue'
import StudentDashboard from './StudentDashboard/index.vue'

export default {
  name: 'dashboard-view',
  components: { VolunteerDashboard, StudentDashboard },
  data() {
    return { isSwitchingRole: false }
  },
  methods: {
    // Switching acting context only -- no identity, role, profile or
    // authentication change, and deliberately no re-login.
    async chooseRole(role) {
      if (this.isSwitchingRole) return
      this.isSwitchingRole = true
      try {
        await this.$store.dispatch('user/setActiveRole', role)
      } finally {
        this.isSwitchingRole = false
      }
    },
  },
  computed: {
    ...mapState({
      user: state => state.user.user,
    }),
    ...mapGetters({
      isAuthenticated: 'user/isAuthenticated',
      isVolunteer: 'user/isVolunteer',
      isDualRole: 'user/isDualRole',
      roleSelectionRequired: 'user/roleSelectionRequired',
    }),

    // Task 12 / STEP-04. Derived from `isVolunteer` -- the frozen acting-context
    // alias -- rather than from a new getter, so the role model is unchanged.
    // This branch only renders once a role has been chosen, so acting context
    // is never null here.
    inactiveRole() {
      return this.isVolunteer ? 'student' : 'volunteer'
    },

    activeRoleLabel() {
      return this.isVolunteer
        ? this.$t('dashboard.roleSwitcher.usingAsVolunteer')
        : this.$t('dashboard.roleSwitcher.usingAsStudent')
    },

    // Names the DESTINATION role, so the pair reads as
    // "Using Borna as Student" / "Switch to Tutor / Volunteer".
    switchRoleLabel() {
      return this.isVolunteer
        ? this.$t('dashboard.roleSwitcher.toStudent')
        : this.$t('dashboard.roleSwitcher.toVolunteer')
    },
  },
}
</script>

<!--
  Task 12 / STEP-04. This file previously had NO style block at all, which is
  why `.role-selection` resolved to nothing and the role gate rendered as bare
  stacked buttons on a white page.
-->
<style lang="scss" scoped>
// --- Role-selection gate ---------------------------------------------------
.role-gate {
  display: flex;
  justify-content: center;
  padding: 40px 20px;
}

.role-gate__card {
  background: white;
  border: 1px solid $c-border-grey;
  border-radius: 12px;
  max-width: 480px;
  padding: 24px;
  width: 100%;

  @include breakpoint-below('tiny') {
    padding: 20px 16px;
  }
}

.role-gate__heading {
  color: $c-soft-black;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
}

.role-gate__body {
  color: $c-secondary-grey;
  font-size: 14px;
  line-height: 1.6;
  margin: 8px 0 0 0;
}

.role-gate__choices {
  margin-top: 20px;
}

// Equal-weight choice cards, matching the LoginView registration role choice.
// Neither option is a primary CTA: presenting one as primary would imply a
// default the product does not have.
.role-choice {
  background: white;
  border: 1px solid $c-border-grey;
  border-radius: 12px;
  cursor: pointer;
  display: block;
  padding: 16px;
  text-align: start;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  width: 100%;

  & + & {
    margin-top: 12px;
  }

  &:hover {
    background-color: $c-backdrop-clear;
    border-color: $c-information-blue;
  }

  &:focus-visible {
    outline: 2px solid $c-information-blue;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
}

.role-choice__label {
  color: $c-soft-black;
  display: block;
  font-size: 16px;
  font-weight: 500;
}

.role-choice__hint {
  color: $c-secondary-grey;
  display: block;
  font-size: 13px;
  line-height: 1.5;
  margin-top: 4px;
}

// --- Active-role bar -------------------------------------------------------
// Aligned with the dashboards' own content padding (40px 20px / 40px) so the
// bar reads as part of the page rather than floating above it.
.role-bar {
  align-items: baseline;
  border-bottom: 1px solid $c-border-grey;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 16px 20px;

  @include breakpoint-above('medium') {
    padding: 16px 40px;
  }
}

// The acting context is stated in words, never by colour or icon alone.
.role-bar__current {
  color: $c-soft-black;
  font-size: 14px;
  font-weight: 500;
  // Keeps the switch action on the same row until the label genuinely runs out
  // of space, at which point flex-wrap drops it to its own line. Physical
  // margins rather than `margin-inline-end`, mirrored under `html.is-rtl`
  // below: the logical property needs Safari 14.1, and this is a mobile-first
  // product.
  margin: 0 12px 0 0;
}

.role-bar__switch {
  font-size: 14px;
}

// --- Direction -------------------------------------------------------------
// `html.is-rtl * { direction: rtl }` in App/index.vue already mirrors the flex
// row itself; only the physical margin above needs flipping. Nesting under
// `html.is-rtl` is the pattern used by the signup and login views.
html.is-rtl {
  .role-bar__current {
    margin: 0 0 0 12px;
  }
}
</style>
